"use client";

import React from "react";
import { MapPin, Phone, Mail, Clock, GraduationCap, Loader } from "lucide-react";
import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Form, FormField, FormItem, FormLabel, FormControl, FormMessage } from "@/components/ui/form";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Link } from "@/components/common/Link";
const contactDetails = [];
function InquiryForm() {
  const {
    isSubmitting,
    isSubmitted,
    submitError,
    successMessage,
    handleSubmit,
    resetForm,
    control
  } = useWvcForm();
  if (isSubmitted && !submitError) {
    return <div className="flex flex-col items-center justify-center py-12 text-center space-y-4">
        <div className="size-12 rounded-lg bg-secondary/20 flex items-center justify-center mb-4">
          <GraduationCap className="size-6 text-secondary" />
        </div>
        <h3 className="text-2xl font-default font-bold text-foreground">Signal Received</h3>
        <p className="text-muted-foreground font-sans max-w-md">
          {successMessage}
        </p>
        <Button onClick={resetForm} variant="outline" className="mt-6 border-primary text-primary hover:bg-primary hover:text-background transition-all">
          Send New Signal
        </Button>
      </div>;
  }
  return <Form onSubmit={handleSubmit} className="space-y-6">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <FormField control={control} name="firstName" rules={{
        required: "First name is required"
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className="font-mono text-xs uppercase tracking-widest text-muted-foreground">First Name</FormLabel>
              <FormControl>
                <Input placeholder="FIRST NAME" {...field} className="font-sans bg-background/50 border-0 border-b border-muted focus-visible:ring-secondary rounded-md" />
              </FormControl>
              <FormMessage className="font-sans" />
            </FormItem>} />
        <FormField control={control} name="lastName" rules={{
        required: "Last name is required"
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className="font-mono text-xs uppercase tracking-widest text-muted-foreground">Last Name</FormLabel>
              <FormControl>
                <Input placeholder="LAST NAME" {...field} className="font-sans bg-background/50 border-0 border-b border-muted focus-visible:ring-secondary rounded-md" />
              </FormControl>
              <FormMessage className="font-sans" />
            </FormItem>} />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <FormField control={control} name="email" rules={{
        required: "Email is required",
        pattern: {
          value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
          message: "Please enter a valid email address"
        }
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className="font-mono text-xs uppercase tracking-widest text-muted-foreground">Email Address</FormLabel>
              <FormControl>
                <Input type="email" placeholder="alex@studio.com" {...field} className="font-sans bg-background/50 border-0 border-b border-muted focus-visible:ring-secondary rounded-md" />
              </FormControl>
              <FormMessage className="font-sans" />
            </FormItem>} />
        <FormField control={control} name="phone" render={({
        field
      }) => <FormItem>
              <FormLabel className="font-mono text-xs uppercase tracking-widest text-muted-foreground">Phone (Optional)</FormLabel>
              <FormControl>
                <Input type="tel" placeholder="+00 000 000 000" {...field} className="font-sans bg-background/50 border-0 border-b border-muted focus-visible:ring-secondary rounded-md" />
              </FormControl>
              <FormMessage className="font-sans" />
            </FormItem>} />
      </div>

      <FormField control={control} name="program" rules={{
      required: "Please select a project type"
    }} render={({
      field
    }) => <FormItem>
            <FormLabel className="font-mono text-xs uppercase tracking-widest text-muted-foreground">Project Type</FormLabel>
            <Select onValueChange={field.onChange} defaultValue={field.value}>
              <FormControl>
                <SelectTrigger className="font-sans bg-background/50 border-0 border-b border-muted focus:ring-secondary rounded-md">
                  <SelectValue placeholder="Select service" />
                </SelectTrigger>
              </FormControl>
              <SelectContent className="bg-popover border-muted">
                <SelectItem value="undergraduate" className="font-sans">Soundtrack</SelectItem>
                <SelectItem value="graduate" className="font-sans">Sound Design</SelectItem>
                <SelectItem value="online" className="font-sans">Music Production</SelectItem>
                <SelectItem value="international" className="font-sans">Mixing & Mastering</SelectItem>
                <SelectItem value="other" className="font-sans">Other</SelectItem>
              </SelectContent>
            </Select>
            <FormMessage className="font-sans" />
          </FormItem>} />

      <FormField control={control} name="message" rules={{
      required: "Please provide your project details"
    }} render={({
      field
    }) => <FormItem>
            <FormLabel className="font-mono text-xs uppercase tracking-widest text-muted-foreground">Project Brief</FormLabel>
            <FormControl>
              <Textarea placeholder="Describe the sonic vision..." className="min-h-[120px] font-sans bg-background/50 border-0 border-b border-muted focus-visible:ring-secondary rounded-md" {...field} />
            </FormControl>
            <FormMessage className="font-sans" />
          </FormItem>} />

      {submitError && <div className="p-4 bg-destructive/10 border border-destructive/20 rounded-md">
          <p className="text-destructive text-sm font-mono">{submitError}</p>
        </div>}

      <Button type="submit" disabled={isSubmitting} className="w-full md:w-auto font-default font-bold bg-primary text-background hover:bg-secondary transition-colors rounded-md px-8 py-6">
        {isSubmitting ? <>
            <Loader className="mr-2 size-4 animate-spin" />
            TRANSMITTING...
          </> : "START PROJECT"}
      </Button>
    </Form>;
}
export default function StartAProject() {
  return <section data-section-id="258192" id="start-a-project" className="bg-background text-foreground py-16 md:py-24 relative overflow-hidden">
      <div className="absolute inset-0 opacity-[0.03] pointer-events-none bg-[url('https://wpvc-images.s3.us-east-1.amazonaws.com/images/1684487/img/noise.png')]"></div>
      <div className="container mx-auto px-4 md:px-6 max-w-7xl relative z-10">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-8 items-start">
          
          <div className="lg:col-span-5 space-y-8">
            <div>
              <h2 className="text-4xl md:text-6xl font-default font-bold text-foreground mb-4 tracking-tighter uppercase">Contact</h2>
              <p className="text-lg text-muted-foreground font-sans leading-relaxed">Whether it's soundtracks, mixing &amp; mastering or anything else, please get in touch via the form. Alternatively contact me via the social media accounts above</p>
            </div>

            <div className="space-y-6">
              {contactDetails.map((item, i) => <div key={i} data-index={i} className="flex items-start gap-4 group">
                  <div className="mt-1 bg-muted p-3 rounded-md shrink-0 group-hover:bg-secondary/20 transition-colors">
                    <item.icon className="size-6 text-primary group-hover:text-secondary transition-colors" />
                  </div>
                  <div>
                    <h3 className="font-mono text-xs uppercase tracking-widest text-muted-foreground mb-1">
                      {item.title}
                    </h3>
                    {item.link ? <Link to={item.link} className="text-foreground hover:text-secondary transition-colors font-default font-semibold text-lg">
                        {item.details}
                      </Link> : <p className="text-foreground font-default font-semibold text-lg">
                        {item.details}
                      </p>}
                  </div>
                </div>)}
            </div>

            <div className="pt-6 border-t border-muted">
              
              <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1684487/img/studio_analog_gear.png" data-wvc-srcset="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1684487/img/studio_analog_gear.png-wvc-srcset" data-wvc-sizes="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1684487/img/studio_analog_gear.png-wvc-sizes" alt="Analog studio equipment" className="w-full h-48 object-cover rounded-md shadow-xl grayscale hover:grayscale-0 transition-all duration-700" loading="lazy" src="https://skilled-urchin.10web.cloud/wp-content/uploads/2026/05/DSC2906-scaled.jpg" />
            </div>
          </div>

          <div className="lg:col-span-7">
            <Card className="flex flex-col gap-6 py-6 shadow-2xl border-muted bg-card/60 backdrop-blur-xl rounded-md text-secondary">
              <CardHeader className="bg-muted/20 border-b border-muted pb-6">
                <CardTitle className="text-2xl font-default font-bold text-foreground uppercase tracking-tight">
                  Project Inquiry
                </CardTitle>
                
              </CardHeader>
              <CardContent className="pt-6">
                <FormProvider formKey="start_project_form_home" formId="start_project_form_home" sectionName="Contact & Inquiry" defaultValues={{
                firstName: "",
                lastName: "",
                email: "",
                phone: "",
                program: "",
                message: ""
              }} submitText="START PROJECT" successMessage="Transmission successful. Your project brief has been received and is currently being reviewed.">
                  <InquiryForm />
                </FormProvider>
              </CardContent>
            </Card>
          </div>

        </div>
      </div>
    </section>;
}